/**
 * The brand record's color rules (logbook decision 26), for the legacy record
 * only.
 *
 * Every surface uses `colors.*` from the record as given and never computes its
 * own. This file exists because the record endpoint is being upgraded: until
 * it is deployed it answers the old shape, `{alias, displayName, brandLogoUrl,
 * brandPrimaryColor}`, with no `colors`, and the map still has to paint
 * something. It is a line-for-line port of keycloak's
 * `public-branding-spi/.../BrandColors.java` (the reservation portal carries
 * the same port), so a legacy record paints exactly what the upgraded
 * endpoint will send for the same primary. Once every answer carries
 * `colors`, this file can go.
 */

/** Parkeze's color, the fallback for any primary that is missing or malformed. */
const PARKEZE_PRIMARY = '#10b981'

/** The dark background `primary_dark` must read on (Tailwind gray-900). */
const DARK_BACKGROUND = '#111827'

/** WCAG 2.1 AA for normal text. */
const MIN_CONTRAST = 4.5

/** The four colors of the record, all lowercase `#rrggbb`. */
interface BrandColors {
  primary: string
  on_primary: string
  primary_text: string
  primary_dark: string
}

/**
 * Lowercase `#rrggbb` for a `#rgb` or `#rrggbb` value (surrounding whitespace
 * ignored), or null for anything else, including the forms with alpha.
 */
export function normalizeHex(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const v = raw.trim().toLowerCase()
  if (/^#[0-9a-f]{6}$/.test(v)) return v
  if (/^#[0-9a-f]{3}$/.test(v)) return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`
  return null
}

/** Derive the record's colors from a raw primary, as the server does. */
export function deriveColors(rawPrimary: unknown): BrandColors {
  const primary = normalizeHex(rawPrimary) ?? PARKEZE_PRIMARY
  return {
    primary,
    on_primary: contrast(primary, '#ffffff') >= contrast(primary, '#000000') ? '#ffffff' : '#000000',
    primary_text: mixToward(primary, '#000000', '#ffffff'),
    primary_dark: mixToward(primary, '#ffffff', DARK_BACKGROUND),
  }
}

/**
 * Mixes `color` toward `target` in sRGB, per channel `round(c + (target - c) * t)`,
 * with `t` from 0 in steps of 0.01, and returns the first mix whose contrast on
 * `background` is at least 4.5. Mixing rather than raising HSL lightness keeps
 * a dark saturated brand recognizable: lightness turned #73000a neon red, a
 * mix toward white gives #b17076. At `t` = 1 the mix is `target` itself.
 */
function mixToward(color: string, target: string, background: string): string {
  const from = toRgb(color)
  const to = toRgb(target)
  for (let step = 0; step <= 100; step++) {
    const t = step / 100
    const candidate = `#${from
      .map((c, i) => Math.round(c + (to[i] - c) * t).toString(16).padStart(2, '0'))
      .join('')}`
    if (contrast(candidate, background) >= MIN_CONTRAST) return candidate
  }
  return target
}

/** WCAG 2.1 contrast ratio between two `#rrggbb` colors, from 1 to 21. */
function contrast(a: string, b: string): number {
  const la = luminance(a)
  const lb = luminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/** WCAG 2.1 relative luminance, with its 0.03928 threshold. */
function luminance(hex: string): number {
  const linear = (channel: number) => {
    const c = channel / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }
  const [r, g, b] = toRgb(hex)
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b)
}

function toRgb(hex: string): [number, number, number] {
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number]
}
